import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { RECORD_MOCKUP, DASHBOARDS } from "@/data/seeData";

const TABS = [
  ["ae", "AE"],
  ["mk", "Market manager"],
  ["co", "Corporate"],
];

function Chart({ v }) {
  const W = 420, H = 170, pl = 40, pb = 22, pt = 10;
  const max = Math.max(...v.bud, ...v.proj) * 1.15;
  const bw = (W - pl - 8) / v.bud.length;
  const y = (x) => pt + (H - pt - pb) * (1 - x / max);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Budget vs projected by month">
      {[0, 1, 2, 3].map((g) => {
        const val = (max / 3) * g;
        return (
          <g key={g}>
            <line x1={pl} x2={W - 4} y1={y(val)} y2={y(val)} stroke="#D6DBDF" />
            <text x={pl - 5} y={y(val) + 3} textAnchor="end" fontSize="10" fill="#5E666D" fontFamily="IBM Plex Mono, monospace">{Math.round(val)}</text>
          </g>
        );
      })}
      {v.bud.map((b, i) => {
        const x = pl + i * bw + bw * 0.18;
        const w = bw * 0.64;
        return (
          <g key={i}>
            <rect x={x} y={y(v.proj[i])} width={w} height={y(0) - y(v.proj[i])} fill="#16181A" fillOpacity="0.3" rx="2" />
            <rect x={x} y={y(v.booked[i])} width={w} height={y(0) - y(v.booked[i])} fill="#16181A" rx="2" />
            <line x1={x - 3} x2={x + w + 3} y1={y(b)} y2={y(b)} stroke="#16181A" strokeWidth="2" />
            <text x={x + w / 2} y={H - 6} textAnchor="middle" fontSize="10" fill="#5E666D" fontFamily="IBM Plex Mono, monospace">{v.months[i]}</text>
          </g>
        );
      })}
    </svg>
  );
}

export default function WhatUsersSee() {
  const [tab, setTab] = useState("ae");
  const v = DASHBOARDS[tab];

  return (
    <section id="see" className="mx-auto max-w-6xl px-4 sm:px-6 mt-14 scroll-mt-16" data-testid="section-see">
      <Reveal>
        <p className="font-mono text-[11.5px] uppercase tracking-[0.08em] text-fog">What users see</p>
        <h2 className="mt-2 font-display text-2xl sm:text-3xl text-coal">The budget record, and the three dashboards</h2>
        <p className="mt-2 text-[15px] text-fog max-w-3xl">
          These are illustrations of the proposed records and dashboards in HubSpot, using
          sample figures. The real ones will be built from MMG's data.
        </p>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="mt-5 bg-white border border-hair rounded-md p-5" data-testid="record-mockup-panel">
          <div className="flex flex-wrap justify-between items-center gap-2.5">
            <h3 className="font-display text-lg text-coal">Monthly Budget record, after the roll-up runs</h3>
            <span className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-warn bg-[#F5E6CC] rounded px-2 py-0.5">Illustrative mockup &middot; sample figures</span>
          </div>
          <div className="mt-4 border border-hair rounded-md overflow-hidden bg-paper">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-hair bg-sunk font-mono text-[11px] text-fog">
              <span className="flex gap-1"><i className="w-2 h-2 rounded-full bg-hair" /><i className="w-2 h-2 rounded-full bg-hair" /><i className="w-2 h-2 rounded-full bg-hair" /></span>
              <span>{RECORD_MOCKUP.header}</span>
            </div>
            <div className="grid lg:grid-cols-[1.1fr_1fr]">
              <div className="p-4 border-b lg:border-b-0 lg:border-r border-hair bg-white">
                <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-fog">Monthly Budget</p>
                <p className="mt-1 font-display text-[17px] text-coal">{RECORD_MOCKUP.recordId}</p>
                <div className="mt-4">
                  {RECORD_MOCKUP.fields.map(([k, val]) => (
                    <div key={k} className="flex justify-between gap-3 py-1.5 border-b border-hair text-[13px] last:border-0">
                      <span className="text-fog">{k}</span>
                      <span className="font-mono text-coal">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-4">
                <div>
                  {RECORD_MOCKUP.financials.map(([k, val, neg]) => (
                    <div key={k} className="flex justify-between gap-3 py-1.5 border-b border-hair text-[13px]">
                      <span className="text-fog">{k} <span className="font-mono text-[9px] text-coal/60">&middot; roll-up</span></span>
                      <span className={`font-mono ${neg ? "text-bad" : "text-coal"}`}>{val}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-[12.5px] text-fog leading-relaxed">
                  <span className="font-mono text-[10px] uppercase bg-[#F5E6CC] text-warn rounded px-1.5 py-0.5 mr-1.5">Optional</span>
                  Showing linked records here means the roll-up also has to create
                  associations. That adds API calls and some hours, so we'd decide in
                  week 1.
                </p>
                {RECORD_MOCKUP.assocs.map((a) => (
                  <div key={a.title} className="mt-3 border border-hair rounded bg-white p-3">
                    <div className="flex justify-between text-[13px] font-medium text-coal">
                      <span>{a.title}</span><span className="font-mono">{a.count}</span>
                    </div>
                    {a.rows.map(([k, val]) => (
                      <div key={k} className="flex justify-between gap-3 mt-1.5 text-[12.5px] text-fog">
                        <span>{k}</span><span className="font-mono">{val}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <p className="mt-3 text-[12.5px] text-fog leading-relaxed">
            Fields tagged &ldquo;roll-up&rdquo; are written by the nightly job. Everything
            in a dashboard is built on these fields, so each report is a simple one-object
            report.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.08}>
        <div className="mt-4 bg-white border border-hair rounded-md p-5" data-testid="dashboard-mockup-panel">
          <div className="flex flex-wrap justify-between items-center gap-2.5">
            <div className="inline-flex border border-hair rounded overflow-hidden bg-white" role="group" aria-label="Dashboard view" data-testid="dashboard-tabs">
              {TABS.map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`dashboard-tab-${k}`}
                  onClick={() => setTab(k)}
                  className={`px-3 py-1.5 font-mono text-[12px] transition-colors duration-200 ${tab === k ? "bg-coal text-white" : "text-fog hover:text-coal"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <span className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-warn bg-[#F5E6CC] rounded px-2 py-0.5">Illustrative mockup &middot; sample figures</span>
          </div>

          <div className="mt-4 border border-hair rounded-md overflow-hidden bg-paper">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-hair bg-sunk font-mono text-[11px] text-fog">
              <span className="flex gap-1"><i className="w-2 h-2 rounded-full bg-hair" /><i className="w-2 h-2 rounded-full bg-hair" /><i className="w-2 h-2 rounded-full bg-hair" /></span>
              <span data-testid="dashboard-title">{v.title}</span>
            </div>
            <div className="p-4 grid gap-3.5">
              <div className="flex gap-1.5 flex-wrap" data-testid="dashboard-filters">
                {v.chips.map(([k, val]) => (
                  <span key={k} className="font-mono text-[11px] border border-hair bg-white rounded px-2 py-1 text-fog">
                    {k}: <b className="text-coal font-medium">{val}</b>
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5" data-testid="dashboard-kpis">
                {v.tiles.map(([l, val, neg]) => (
                  <div key={l} className="bg-white border border-hair rounded px-3 py-2.5">
                    <p className="text-[11.5px] text-fog">{l}</p>
                    <p className={`mt-0.5 font-mono text-[18px] ${neg ? "text-bad" : "text-coal"}`}>{val}</p>
                  </div>
                ))}
              </div>
              <div className="grid lg:grid-cols-[1.3fr_1fr] gap-2.5">
                <div className="bg-white border border-hair rounded p-3">
                  <p className="text-[12.5px] font-medium text-coal">Budget vs projected by month ($k)</p>
                  <Chart v={v} />
                  <div className="flex gap-4 flex-wrap text-[12px] text-fog">
                    <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-[2px] bg-coal inline-block" /> Booked</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-[2px] bg-coal/30 inline-block" /> + weighted pending</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-3 h-[3px] bg-coal inline-block" /> Budget</span>
                  </div>
                </div>
                <div className="bg-white border border-hair rounded p-3">
                  <p className="text-[12.5px] font-medium text-coal">{v.tt}</p>
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-[13px]" data-testid="dashboard-table">
                      <thead>
                        <tr>
                          {v.cols.map((c, i) => (
                            <th key={c} className={`py-1.5 text-left font-mono text-[10.5px] uppercase tracking-[0.05em] text-fog font-normal ${i ? "text-right" : ""}`}>{c}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {v.rows.map((r) => (
                          <tr key={r[0]} className="border-t border-hair">
                            {r.map((c, i) => (
                              <td key={i} className={`py-2 ${i ? "text-right font-mono" : "text-coal"} ${i === 3 ? (c[0] === "-" ? "text-bad" : "text-emerald-700") : i ? "text-coal/80" : ""}`}>{c}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-3 text-[12.5px] text-fog leading-relaxed">
            Each dashboard is a set of connected reports, not one spreadsheet-style grid.
            Drill-down works by filtering on market, AE, station or category, or by
            clicking through to the records. The combined chart and the market-only
            visibility are confirmed in a week 1 sandbox test before we commit to this
            layout (see Risks).
          </p>
        </div>
      </Reveal>
    </section>
  );
}
